import { WhatsappLogo } from '@phosphor-icons/react';
import useWhatsAppMessage from '@/hooks/useWhatsAppMessage';

/**
 * Returns the digits-only international number suitable for wa.me,
 * or null when the input isn't a valid international phone.
 *
 * wa.me requires: digits only, no +, no spaces, country code included.
 * We accept anything that starts with + or a long run of digits (≥11).
 */
export function toWhatsAppNumber(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  const digits = s.replace(/\D/g, '');
  if (!digits) return null;
  // Require international format: either the user typed '+', or we have
  // ≥ 11 digits (country code + local). Reject short national numbers
  // (9 digits like 966814333 without country code).
  if (s.startsWith('+') || digits.length >= 11) return digits;
  return null;
}

/**
 * Small inline WhatsApp quick-action button. Renders nothing when the number
 * cannot be converted to an international format.
 *
 * Mensagem (prioridade):
 *   1. `idNegocio` → faz lookup ao backend (template configurado em Backoffice).
 *   2. `message` prop literal — usado quando não há contexto de lead (ex: ficha cliente).
 *   3. Default: nenhuma mensagem (link directo).
 */
export default function WhatsAppLink({ phone, size = 14, className = '', message = '', idNegocio = null, firstName = '', onOpen = null }) {
  const dynamicMessage = useWhatsAppMessage(idNegocio, message, firstName);
  const finalMessage = idNegocio ? dynamicMessage : message;
  const n = toWhatsAppNumber(phone);
  if (!n) return null;
  const href = `https://wa.me/${n}${finalMessage ? `?text=${encodeURIComponent(finalMessage)}` : ''}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(e) => { e.stopPropagation(); if (onOpen) { try { onOpen(); } catch (_e) { /* silent */ } } }}
      data-testid={`whatsapp-link-${n}`}
      title="Abrir conversa no WhatsApp"
      className={`inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#25D366] text-white hover:bg-[#1DA851] transition-colors ${className}`}
    >
      <WhatsappLogo size={size} weight="fill" />
    </a>
  );
}
